import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Avatar,
  Checkbox,
  Button,
  Menu,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  StepConnector,
  styled,
  Stack,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState } from "react";
import { diffWordsWithSpace } from "diff";
import { marked } from "marked";
import { Target } from "lucide-react";
import { sanitizeDisplayHtml } from "../utils/sanitizeHtml";

const CustomConnector = styled(StepConnector)(() => ({
  "&.MuiStepConnector-root": {
    top: 18,
    left: "calc(50% + 17px)",
    right: "calc(50% - 17px)",
  },
  "& .MuiStepConnector-line": {
    borderLeftWidth: 2,
    borderColor: "#b0b7be",
    minHeight: 40,
    marginLeft: "12px",
    marginRight: "auto",
  },
}));

export default function VersionHistoryDrawer({
  open,
  onClose,
  versions = [],
  currentVersionIdx = 0,
  selectedVersionIdxs = [],
  onCheckboxChange,
  onRestoreVersion,
}) {
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareContent, setCompareContent] = useState({
    old: "",
    new: "",
    newUpdatedAt: null,
  });
  // Local menu anchor and index for drawer menu only
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuVersionIdx, setMenuVersionIdx] = useState(null);
  // Restore dialog state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreVersionContent, setRestoreVersionContent] = useState(null);
  // Show File dialog state
  const [showFileDialogOpen, setShowFileDialogOpen] = useState(false);
  const [showFileVersion, setShowFileVersion] = useState(null);

  const handleCheckboxChange = (idx) => {
    onCheckboxChange(idx);
  };

  const handleCompare = () => {
    if (selectedVersionIdxs.length === 2) {
      const [i1, i2] = selectedVersionIdxs;
      setCompareContent({
        old: versions[i1].rawMarkdown || "",
        new: versions[i2].rawMarkdown || "",
        newUpdatedAt: versions[i2].updatedAt || null,
      });
      setCompareOpen(true);
    }
  };

  // Menu open/close logic for drawer only
  const handleMenuOpen = (event, idx) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuVersionIdx(idx);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuVersionIdx(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", md: 530 },
          maxWidth: {
            xs: "100vw",
            md: "calc(100vw - var(--prospo-sidecar-width, 0px))",
          },
          right: { xs: 0, md: "var(--prospo-sidecar-width, 0px)" },
          height: "calc(100vh - 65px)",
          mt: "62px",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: (theme) =>
            theme.transitions.create(["right", "width", "max-width"], {
              duration: theme.transitions.duration.standard,
            }),
          "& .MuiBox-root": { px: 3 },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 3,
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Version History
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      {/* Timeline List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 6,
          position: "relative",
          scrollbarWidth: "none",
        }}
      >
        <Stepper
          orientation="vertical"
          connector={<CustomConnector />}
          sx={{
            pt: 2,
            pb: 2,
            "& .MuiStep-root": { minHeight: 20 },
            "& .MuiStepLabel-root": {
              py: 0,
              pl: 2, // Add left padding to bring content closer to icon
            },
            "& .MuiStepLabel-labelContainer": {
              mt: 2,
              ml: -4, // Move content closer to the icon
            },
          }}
          activeStep={currentVersionIdx}
        >
          {versions.map((version, idx) => (
            <Step key={version._id || idx}>
              <StepLabel
                icon={
                  <Target
                    size={24}
                    color={idx === 0 ? "#26C0E2" : "#b0b7be"}
                    style={{ marginLeft: -0.5 }}
                  />
                }
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      fontSize={idx === currentVersionIdx ? 16 : 14}
                      fontWeight={idx === currentVersionIdx ? "bold" : "normal"}
                      sx={{
                        color: idx === currentVersionIdx ? "#222" : "#222",
                      }}
                    >
                      {idx === currentVersionIdx
                        ? "Current Version"
                        : `${new Date(version.updatedAt).toLocaleTimeString(
                            [],
                            { hour: "2-digit", minute: "2-digit" }
                          )}, ${new Date(
                            version.updatedAt
                          ).toLocaleDateString()}`}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent={"center"}
                      gap={1}
                      mt={0.5}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                        }}
                      >
                        {version.updatedBy?.charAt(0) || "U"}
                      </Avatar>
                      <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{
                          pb: 0.2,
                        }}
                      >
                        {version.updatedBy
                          .split(" ")
                          .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
                          .join(" ") || "User not found"}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Stack direction="row" alignItems="center">
                    <Checkbox
                      size="small"
                      checked={selectedVersionIdxs.includes(idx)}
                      onChange={() => handleCheckboxChange(idx)}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, idx)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleCompare}
          variant="contained"
          disabled={selectedVersionIdxs.length !== 2}
        >
          Compare Versions
        </Button>
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setShowFileVersion(versions[menuVersionIdx]);
            setShowFileDialogOpen(true);
            handleMenuClose();
          }}
        >
          Show File
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRestoreVersionContent(versions[menuVersionIdx]);
            setRestoreDialogOpen(true);
            handleMenuClose();
          }}
        >
          Restore
        </MenuItem>
      </Menu>
      {/* Show File Dialog */}
      <Dialog
        open={showFileDialogOpen}
        onClose={() => setShowFileDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} fontSize={20}>
              Version Content
            </Typography>
            {showFileVersion?.updatedAt && (
              <Typography fontSize={14} color="text.secondary" sx={{ mt: 0.5 }}>
                {new Date(showFileVersion.updatedAt).toLocaleString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setShowFileDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              border: "1px solid #eee",
              borderRadius: 2,
              background: "#fafbfc",
              padding: 3,
              minHeight: "400px",
              maxHeight: "600px",
              overflow: "auto",
            }}
          >
            <div
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                lineHeight: "1.6",
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizeDisplayHtml(
                  (() => {
                    const markdown = showFileVersion?.rawMarkdown || "";
                    const result = marked.parse
                      ? marked.parse(markdown)
                      : marked(markdown);
                    return typeof result === "string" ? result : "";
                  })(),
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowFileDialogOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Restore Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Restore Version</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore this version? This will replace the
            original content of the summary.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (onRestoreVersion) onRestoreVersion(restoreVersionContent);
              setRestoreDialogOpen(false);
            }}
            color="primary"
            variant="contained"
          >
            Yes, Restore
          </Button>
        </DialogActions>
      </Dialog>
      {/* Compare Versions Dialog */}
      <Dialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0,
          }}
        >
          <Typography fontWeight={600} fontSize={20}>
            Compare versions
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Checkbox checked disabled sx={{ p: 0, mr: 1 }} />
            <Typography fontSize={14} color="text.secondary">
              Highlight changes
            </Typography>
            <IconButton onClick={() => setCompareOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }} />
          {(() => {
            // Convert markdown to HTML synchronously
            const getHtmlSync = (markdown: string) => {
              // marked.parse may return a Promise in some versions, so handle both cases
              const result = marked.parse
                ? marked.parse(markdown)
                : marked(markdown);
              if (typeof result === "string") {
                return result;
              }
              // If it's a Promise, this is not supported in this sync context, fallback to empty string
              return "";
            };

            const oldHtml = getHtmlSync(compareContent.old || "");
            const newHtml = getHtmlSync(compareContent.new || "");

            // Diff logic (works on HTML now, but you can adjust to diff markdown if preferred)
            const diff = diffWordsWithSpace(
              oldHtml.replace(/<[^>]+>/g, ""), // strip tags for diff
              newHtml.replace(/<[^>]+>/g, "")
            );

            // Remove unnecessary escape characters in template strings
            const oldContent = diff
              .map((part) =>
                part.removed
                  ? `<span style="background:#ffcdd2; color:#d32f2f">${part.value}</span>`
                  : !part.added
                  ? part.value
                  : ""
              )
              .join("");
            const newContent = diff
              .map((part) =>
                part.added
                  ? `<span style="background:#c8e6c9; color:#2e7d32">${part.value}</span>`
                  : !part.removed
                  ? part.value
                  : ""
              )
              .join("");

            // Date/time logic
            let v1Time = "",
              v2Time = "";
            let v1Idx = -1,
              v2Idx = -1;
            if (selectedVersionIdxs.length === 2) {
              const [firstIdx, secondIdx] = [...selectedVersionIdxs].sort(
                (a, b) => a - b
              );
              const v1 = versions[firstIdx];
              const v2 = versions[secondIdx];
              v1Idx = firstIdx;
              v2Idx = secondIdx;

              const formatDate = (dateString) =>
                new Date(dateString).toLocaleString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                });

              v1Time = v1?.updatedAt ? formatDate(v1.updatedAt) : "";
              v2Time = v2?.updatedAt ? formatDate(v2.updatedAt) : "";
            }

            return (
              <div style={{ display: "flex", gap: 16 }}>
                <div
                  style={{
                    flex: 1,
                    border: "1px solid #eee",
                    borderRadius: 6,
                    background: "#fafbfc",
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 20,
                      border: "3px solid #eee",
                      borderRadius: 6,
                      background: "#eee",
                      padding: 10,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {v1Idx === 0 ? (
                      <img
                        src="/active.svg"
                        alt="Active"
                        style={{ width: 22, height: 22 }}
                      />
                    ) : (
                      <img
                        src="/inactive.svg"
                        alt="Inactive"
                        style={{ width: 22, height: 22 }}
                      />
                    )}
                    {v1Idx === 0 ? (
                      <span>Current version</span>
                    ) : (
                      <span style={{ fontSize: 16 }}>{v1Time}</span>
                    )}
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "inherit",
                      lineHeight: "1.6",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeDisplayHtml(
                        oldContent
                          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                          .replace(/\n• /g, "\n• "),
                      ),
                    }}
                  />
                </div>
                <div
                  style={{
                    flex: 1,
                    border: "1px solid #eee",
                    borderRadius: 6,
                    background: "#fafbfc",
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 20,
                      border: "3px solid #eee",
                      borderRadius: 6,
                      background: "#eee",
                      padding: 10,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {v2Idx === 0 ? (
                      <img
                        src="/active.svg"
                        alt="Active"
                        style={{ width: 22, height: 22 }}
                      />
                    ) : (
                      <img
                        src="/inactive.svg"
                        alt="Inactive"
                        style={{ width: 22, height: 22 }}
                      />
                    )}
                    {v2Idx === 0 ? (
                      <span>Current version</span>
                    ) : (
                      <span style={{ fontSize: 16 }}>{v2Time}</span>
                    )}
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "inherit",
                      lineHeight: "1.6",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeDisplayHtml(
                        newContent
                          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                          .replace(/\n• /g, "\n• "),
                      ),
                    }}
                  />
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}
